import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql/gql';
import { useAuth0 } from '../auth/AuthProvider';
import { graphqlRequest, shouldRetryQuery } from '../utils/graphqlClient';
import { isGraphQLError } from '../utils/errorUtils';

const myPartnerQuery = graphql(/* GraphQL */ `
  query myPartner {
    myPartner {
      partnerId
      name
      status
      kycState
      stripeConnectAccountId
    }
  }
`);

const useGetPartner = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  type PartnerData = {
    myPartner: {
      partnerId: number;
      name: string;
      status: string;
      kycState: string;
      stripeConnectAccountId: string;
    } | null;
  };

  const { data, isLoading, error } = useQuery<PartnerData>({
    queryKey: ['partner'],
    queryFn: async (): Promise<PartnerData> => {
      try {
        const result = await graphqlRequest<PartnerData>(
          myPartnerQuery,
          undefined,
          getAccessTokenSilently,
        );
        // If myPartner is null, it means user doesn't have a partner account
        // This is not an error, just return the result
        return result;
      } catch (err: unknown) {
        // Check if it's an authorization error for myPartner being null
        if (isGraphQLError(err)) {
          const hasMyPartnerAuthError = err.response?.errors?.some(
            e =>
              e?.extensions?.code === 'AUTH_NOT_AUTHORIZED' &&
              e?.path?.includes('myPartner'),
          );
          if (hasMyPartnerAuthError) {
            // User is authenticated but doesn't have a partner account
            // Return null instead of throwing error
            return { myPartner: null };
          }
        }
        throw err;
      }
    },
    enabled: isAuthenticated,
    staleTime: 300000, // 5 minutes
    retry: shouldRetryQuery,
  });

  return {
    partner: data?.myPartner,
    isLoading,
    isError: !!error,
    error,
  };
};

export default useGetPartner;
